import React, { useMemo, useState, useRef } from 'react';
import { CallbackRequestColumns, PROSPECT_INFO_COLUMNS, SessionV2Columns } from 'interfaces/table';
import { ChevronLeft, ChevronRight, Calendar, Clock } from 'lucide-react';
import { useSessionFetch } from 'lib/redux/sessions/lists/session-fetch-hook';
import { useUserData } from '../../lib/redux/users/user-data-hook';
import { SessionListProps as SessionFetchProps } from 'lib/redux/sessions/lists/cfg';

type CallbackProps = SessionFetchProps & {
  onRowClick: (sessionId: string) => void;
};

type CallbackItem = {
  sessionId: string;
  timing: Date;
  status: string;
  reasoning: string;
  name?: string;
  phone?: string | null;
  resourceId?: string;
};

type ViewType = 'month' | 'week' | 'day';

const formatTime = (date: Date): string => {
  return new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  }).format(date);
};

const getStatusColor = (status: string): string => {
  const statusKey = status?.toUpperCase() || '';
  switch (statusKey) {
    case 'BRUSH_OFF':
      return 'bg-red-50 text-red-700 border-red-200';
    case 'REFERRAL':
      return 'bg-green-50 text-green-700 border-green-200';
    case 'BUSY':
      return 'bg-yellow-50 text-yellow-700 border-yellow-200';
    case 'EXPLICIT_CALLBACK_TIME':
      return 'bg-blue-50 text-blue-700 border-blue-200';
    case 'GATEKEEPER_CALLBACK':
      return 'bg-purple-50 text-purple-700 border-purple-200';
    case 'GATEKEEPER_NO_CALLBACK':
      return 'bg-gray-50 text-gray-700 border-gray-200';
    case 'NURTURE':
      return 'bg-indigo-50 text-indigo-700 border-indigo-200';
    default:
      return 'bg-gray-50 text-gray-700 border-gray-200';
  }
};

export function Callback({ onRowClick, ...sessionFetchProps }: CallbackProps) {
  const [currentDate, setCurrentDate] = useState(() => new Date());
  const [view, setView] = useState<ViewType>('week');
  const [hoveredCallbackId, setHoveredCallbackId] = useState<string | null>(null);
  const [hoverPosition, setHoverPosition] = useState({ top: 0, left: 0 });
  
  // Refs for measuring callback items
  const callbackRefs = useRef<Map<string, HTMLDivElement>>(new Map());

  // Use session fetch hook similar to SessionList
  const { items } = useSessionFetch(sessionFetchProps);

  // Get active dialer users for rep names
  const { allActiveDialerUsers } = useUserData();

  // Process items into callback format
  const callbacks: CallbackItem[] = useMemo(() => {
    return items
      .map(item => ({
        sessionId: item.session_id,
        timing: item.result.get(CallbackRequestColumns.CALLBACK_TIMING) as Date,
        status: item.result.get(CallbackRequestColumns.CALLBACK_STATUS) as string,
        reasoning: item.result.get(CallbackRequestColumns.CALLBACK_REASONING) as string,
        name: item.result.get(PROSPECT_INFO_COLUMNS.PROSPECT_NAME) as string,
        phone: item.customer_address,
        resourceId: item.result.get(SessionV2Columns.RESOURCE_ID) as string
      }))
      .filter(item => item.timing)
      .sort((a, b) => a.timing.getTime() - b.timing.getTime());
  }, [items]);

  // Group callbacks by date
  const callbacksByDate = useMemo(() => {
    const grouped = new Map<string, CallbackItem[]>();
    callbacks.forEach(callback => {
      const dateKey = callback.timing.toDateString();
      if (!grouped.has(dateKey)) {
        grouped.set(dateKey, []);
      }
      const dayCallbacks = grouped.get(dateKey)!;
      dayCallbacks.push(callback);
    });
    return grouped;
  }, [callbacks]);

  // Get rep name helper function
  const getRepName = (resourceId?: string): string => {
    if (!resourceId || !allActiveDialerUsers) return 'Unknown Rep';
    const rep = allActiveDialerUsers.find(u => u.user_id === resourceId);
    return rep?.user_name || 'Unknown Rep';
  };

  // Handle callback hover
  const handleCallbackHover = (
    event: React.MouseEvent<HTMLDivElement>, 
    callbackId: string
  ) => {
    const element = event.currentTarget;
    const rect = element.getBoundingClientRect();
    
    // Position tooltip above the callback item
    setHoverPosition({ 
      top: rect.top - 10, 
      left: rect.left + rect.width / 2
    });
    
    setHoveredCallbackId(callbackId);
  };

  // Navigation functions
  const navigate = (direction: 'prev' | 'next') => {
    setCurrentDate(prev => {
      const date = new Date(prev);
      switch (view) {
        case 'month':
          date.setMonth(date.getMonth() + (direction === 'next' ? 1 : -1));
          break;
        case 'week':
          date.setDate(date.getDate() + (direction === 'next' ? 7 : -7));
          break;
        case 'day':
          date.setDate(date.getDate() + (direction === 'next' ? 1 : -1));
          break;
      }
      return date;
    });
  };

  // Generate calendar grid
  const calendarDays = useMemo(() => {
    const days = [];
    
    switch (view) {
      case 'month': {
        const year = currentDate.getFullYear();
        const month = currentDate.getMonth();
        const firstDay = new Date(year, month, 1);
        const lastDay = new Date(year, month + 1, 0);
        
        // Add padding for start of month
        const startPadding = firstDay.getDay();
        for (let i = 0; i < startPadding; i++) {
          days.push(null);
        }
        
        // Add all days of the month
        for (let day = 1; day <= lastDay.getDate(); day++) {
          days.push(new Date(year, month, day));
        }
        break;
      }
      case 'week': {
        const curr = new Date(currentDate);
        const first = curr.getDate() - curr.getDay();
        for (let i = 0; i < 7; i++) {
          const date = new Date(curr);
          date.setDate(first + i);
          days.push(date);
        }
        break;
      }
      case 'day': {
        days.push(currentDate);
        break;
      }
    }
    
    return days;
  }, [currentDate, view]);

  const renderMonthDay = (date: Date | null, index: number) => {
    if (!date) {
      return <div key={`empty-${index}`} className="p-2 border-b border-r bg-gray-50/30" />;
    }

    const dateKey = date.toDateString();
    const dayCallbacks = callbacksByDate.get(dateKey) || [];
    const isToday = new Date().toDateString() === dateKey;
    const hasCallbacks = dayCallbacks.length > 0;

    return (
      <div 
        key={dateKey} 
        onClick={() => {
          if (hasCallbacks) {
            setCurrentDate(date);
            setView('day');
          }
        }}
        className={`relative p-2 border-b border-r h-20 group flex flex-col items-center justify-center ${
          isToday ? 'bg-blue-50' : ''
        } ${hasCallbacks ? 'hover:bg-gray-50 cursor-pointer' : ''}`}
      >
        <div className={`absolute top-1 right-2 text-sm ${
          isToday ? 'font-bold text-blue-600' : 'text-gray-600'
        }`}>
          {date.getDate()}
        </div>
        
        {hasCallbacks && (
          <>
            <div className={`flex items-center justify-center h-9 w-9 rounded-full text-white text-sm font-medium transition-colors ${
              dayCallbacks.length > 3 ? 'bg-indigo-600' :
              dayCallbacks.length > 1 ? 'bg-indigo-500' : 'bg-indigo-400'
            }`}>
              {dayCallbacks.length}
            </div>
            
            {/* Hover preview - shown below the item */}
            <div className="absolute z-[100] invisible group-hover:visible w-72 bg-white border border-gray-200 shadow-xl rounded-lg p-3 top-full left-1/2 transform -translate-x-1/2 mt-1">
              <div className="font-medium text-sm text-gray-900 mb-2 pb-1 border-b">
                {date.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })}
              </div>
              <div className="space-y-1.5 max-h-48 overflow-y-auto">
                {dayCallbacks.map(callback => (
                  <div 
                    key={callback.sessionId} 
                    className="flex items-center justify-between text-xs p-1.5 hover:bg-gray-50 rounded"
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="font-medium whitespace-nowrap">{formatTime(callback.timing)}</span>
                      <span className="text-gray-600 truncate max-w-[120px]">{callback.name || callback.phone || 'Unknown'}</span>
                    </div>
                    <span className={`px-1.5 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ml-2 ${getStatusColor(callback.status)}`}>
                      {callback.status}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
      </div>
    );
  };

  const renderCallbackItem = (callback: CallbackItem) => (
    <div
      key={callback.sessionId}
      ref={el => el && callbackRefs.current.set(callback.sessionId, el)}
      onClick={() => onRowClick(callback.sessionId)}
      onMouseEnter={(e) => handleCallbackHover(e, callback.sessionId)}
      onMouseLeave={() => setHoveredCallbackId(null)}
      className="relative w-full text-left p-3 text-sm rounded-md bg-white hover:bg-gray-50 border border-gray-200 hover:border-gray-300 cursor-pointer transition-all"
    >
      <div className="flex flex-col gap-2">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <span className="font-medium whitespace-nowrap">{formatTime(callback.timing)}</span>
            <span className="text-gray-600 truncate">{callback.name || callback.phone || 'Unknown'}</span>
          </div>
          <span className={`px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${getStatusColor(callback.status)}`}>
            {callback.status}
          </span>
        </div>
        <div className="flex items-center">
          <span className="text-xs text-gray-500 mr-1">Rep:</span>
          <span className="text-xs font-medium text-gray-700 truncate">{getRepName(callback.resourceId)}</span>
        </div>
      </div>
    </div>
  );

  return (
    <div className="h-full flex flex-col bg-white">
      {/* Header */}
      <div className="flex items-center justify-between p-3 border-b bg-white sticky top-0 z-10">
        <div className="flex items-center gap-2 min-w-0 flex-shrink-1">
          <button 
            onClick={() => navigate('prev')}
            className="p-1 hover:bg-gray-100 rounded-full transition-colors flex-shrink-0"
            aria-label="Previous period"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <h2 className="text-sm font-semibold truncate">
            {view === 'month' 
              ? currentDate.toLocaleString('default', { month: 'long', year: 'numeric' })
              : view === 'week'
                ? `Week of ${currentDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`
                : currentDate.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })}
          </h2>
          <button 
            onClick={() => navigate('next')}
            className="p-1 hover:bg-gray-100 rounded-full transition-colors flex-shrink-0"
            aria-label="Next period"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>

        <div className="flex items-center gap-0.5 border rounded-lg bg-gray-50 p-0.5 shadow-sm flex-shrink-0">
          <button
            onClick={() => setView('month')}
            className={`px-2 py-0.5 rounded-md text-xs flex items-center gap-1 transition-colors min-w-[60px] ${
              view === 'month' ? 'bg-white shadow text-indigo-600 font-medium' : 'text-gray-600 hover:bg-gray-100'
            }`}
            aria-label="Month view"
          >
            <Calendar className="w-3 h-3" />
            <span>Month</span>
          </button>
          <button
            onClick={() => setView('week')}
            className={`px-2 py-0.5 rounded-md text-xs flex items-center gap-1 transition-colors min-w-[55px] ${
              view === 'week' ? 'bg-white shadow text-indigo-600 font-medium' : 'text-gray-600 hover:bg-gray-100'
            }`}
            aria-label="Week view"
          >
            <Calendar className="w-3 h-3" />
            <span>Week</span>
          </button>
          <button
            onClick={() => setView('day')}
            className={`px-2 py-0.5 rounded-md text-xs flex items-center gap-1 transition-colors min-w-[50px] ${
              view === 'day' ? 'bg-white shadow text-indigo-600 font-medium' : 'text-gray-600 hover:bg-gray-100'
            }`}
            aria-label="Day view"
          >
            <Clock className="w-3 h-3" />
            <span>Day</span>
          </button>
        </div>
      </div>

      {/* Calendar Grid */}
      <div className="flex-1 overflow-auto">
        {view === 'month' ? (
          <div className="grid grid-cols-7 text-sm">
            {/* Day headers */}
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
              <div key={day} className="p-2 text-center font-medium border-b bg-gray-50 text-gray-600">
                {day}
              </div>
            ))}

            {/* Calendar days */}
            {calendarDays.map((date, index) => renderMonthDay(date, index))}
          </div>
        ) : view === 'week' ? (
          <div className="divide-y">
            {calendarDays.map((date) => {
              if (!date) return null;
              const dateKey = date.toDateString();
              const dayCallbacks = callbacksByDate.get(dateKey) || [];
              const isToday = new Date().toDateString() === dateKey;

              return (
                <div 
                  key={dateKey}
                  className={`px-4 py-3 ${isToday ? 'bg-blue-50' : ''}`}
                >
                  <div className="font-medium mb-2 text-gray-900">
                    {date.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })}
                  </div>
                  {dayCallbacks.length > 0 ? (
                    <div className="space-y-2">
                      {dayCallbacks.map(callback => renderCallbackItem(callback))}
                    </div>
                  ) : (
                    <div className="text-center text-gray-500 text-xs italic py-1">
                      No callbacks scheduled
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <div className="p-4">
            <div className="space-y-2">
              {(callbacksByDate.get(currentDate.toDateString()) || []).length > 0 ? (
                (callbacksByDate.get(currentDate.toDateString()) || []).map(callback => renderCallbackItem(callback))
              ) : (
                <div className="text-center text-gray-500 italic py-4">
                  No callbacks scheduled for this day
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Global tooltip for callback hover */}
      {hoveredCallbackId && callbacks.find(c => c.sessionId === hoveredCallbackId)?.reasoning && (
        <div 
          className="fixed z-[100] bg-gray-900 text-white text-xs p-2 rounded-md shadow-lg max-w-xs transform -translate-x-1/2 -translate-y-full"
          style={{ 
            top: `${hoverPosition.top}px`, 
            left: `${hoverPosition.left}px` 
          }}
        >
          {callbacks.find(c => c.sessionId === hoveredCallbackId)?.reasoning}
        </div>
      )}
    </div>
  );
}
